import axios, { AxiosResponse } from "axios";
import { API_URL, STREAMING_API_URL } from "../constants";
import { UserInfo } from "../contexts/UserInfoContext";
import {
  GetChatHistoryRes,
  GetSessionRes,
  StreamMessageTypes,
} from "../models/Chat";
import { checkSessionExpired } from "./Auth";

export async function postChatMessage(
  message: string,
  chatId: string,
  accessToken: string,
  publicationFromYear: number | null,
  publicationToYear: number | null,
  documentTypes: string[],
  authorFilter: string | null,
  isWebToolEnabled: boolean,
  onStream: (data: StreamMessageTypes) => void,
  onError: (error: any) => void,
  onComplete: () => void
): Promise<void> {
  if (!checkSessionExpired(accessToken)) {
    try {
      const requestBody = {
        question: message,
        chatId,
        isWebToolEnabled,
        ...(publicationFromYear !== null && { publicationFromYear }),
        ...(publicationToYear !== null && { publicationToYear }),
        ...(documentTypes?.length ? { documentTypes } : {}),
        ...(authorFilter?.trim() ? { authorFilter: authorFilter.trim() } : {}),
      };

      const response = await fetch(`${STREAMING_API_URL}stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      // Buffer to handle arbitrary chunk boundaries (JSON can span chunks)
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          onComplete();
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process complete lines; keep the last partial line in the buffer.
        const parts = buffer.split("\n");
        buffer = parts.pop() ?? "";
        const lines = parts.filter((line) => line.trim());

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const streamedData = JSON.parse(line.slice(6));
              onStream(streamedData);

              // Defensive: if backend ever signals completion early, stop reading.
              if (streamedData.type === "final_response") {
                onComplete();
                return;
              }
            } catch (error) {
              console.error("Error parsing stream data:", error);
            }
          }
        }
      }
    } catch (error) {
      // Important: if the stream errors after we’ve flipped UI state (e.g. "Loading sources"),
      // ensure the caller resets loading indicators.
      onError(error);
      onComplete();
    }
  }
}

export async function getChatSession(
  userInfo: UserInfo,
  chatId: string
): Promise<GetSessionRes | undefined> {
  if (!checkSessionExpired(userInfo.accessToken)) {
    const headers = {
      Authorization: `Bearer ${userInfo.accessToken}`,
    };
    try {
      const response: AxiosResponse = await axios.get(
        `${API_URL}chat/${chatId}`,
        {
          headers: headers,
        }
      );

      return response.data;
    } catch (error) {
      return undefined;
    }
  } else return undefined;
}

export async function deleteChatSession(
  chatId: string,
  accessToken: string
): Promise<any> {
  if (!checkSessionExpired(accessToken)) {
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    try {
      const response: AxiosResponse = await axios.delete(
        `${API_URL}chat/${chatId}`,
        {
          headers: headers,
        }
      );

      return response.data;
    } catch (error) {
      return undefined;
    }
  } else return undefined;
}

export async function putResponseRating(
  chatId: string,
  exchangeId: string,
  rating: number | null,
  accessToken?: string
): Promise<any> {
  if (!checkSessionExpired(accessToken)) {
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    const request = { rating };
    return new Promise((resolve, reject) => {
      axios
        .put(
          `${API_URL}chat/${chatId}/exchange/${exchangeId}/rating`,
          request,
          { headers: headers }
        )
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  } else return undefined;
}

export async function getChatHistory(
  userInfo: UserInfo
): Promise<GetChatHistoryRes | undefined> {
  if (!checkSessionExpired(userInfo.accessToken)) {
    const headers = {
      Authorization: `Bearer ${userInfo.accessToken}`,
    };
    try {
      const response = await axios.get(`${API_URL}chats`, {
        headers: headers,
      });

      return response.data;
    } catch (error) {
      return undefined;
    }
  } else return undefined;
}

export async function renameChatSession(
  chatId: string,
  chatTitle: string,
  accessToken: string
): Promise<any> {
  if (!checkSessionExpired(accessToken)) {
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    try {
      const response: AxiosResponse = await axios.put(
        `${API_URL}chat/${chatId}/rename`,
        { chatTitle },
        {
          headers: headers,
        }
      );

      return response.data;
    } catch (error) {
      return undefined;
    }
  } else return undefined;
}

export async function searchChatSessions(
  query: string,
  limit: number = 20,
  accessToken: string,
  includeHighlights: boolean = true,
  dateFilter?: {
    start_date?: string;
    end_date?: string;
  }
): Promise<any> {
  if (!checkSessionExpired(accessToken)) {
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };

    const requestBody: any = {
      query: query.trim(),
      limit,
      include_highlights: includeHighlights,
    };

    if (dateFilter) {
      requestBody.date_filter = dateFilter;
    }

    try {
      const response: AxiosResponse = await axios.post(
        `${API_URL}sessions/search`,
        requestBody,
        {
          headers: headers,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error searching chat sessions:", error);
      return undefined;
    }
  } else return undefined;
}
